import React, { useState, useContext, useEffect } from 'react';
import { Line, Bar } from "react-chartjs-2";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	BarElement,
	Title,
	Tooltip,
	Legend,
	ArcElement,
} from "chart.js";
import DateRangePickerComponent from "../../../components/DaterangePicker";
import { ProductContext } from '../../../store';
import { format, startOfMonth, addDays, isBefore, isEqual } from "date-fns";
import { Col, Row, Spinner, Card, CardBody } from "reactstrap";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import CredentialSkeleton from "../../../components/Skeleton/CredentialSkeleton";
import { PostData } from "../../../services/FetchData";
import { toast } from "react-toastify";
import { getActiveWebsiteId } from "../../../utils/reportUtils";

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	BarElement,
	Title,
	Tooltip,
	Legend,
	ArcElement
);

const options = {
	responsive: true,
	maintainAspectRatio: false,
	plugins: {
		legend: {
			display: true,
			position: 'top',
		},
	},
	scales: {
		x: {
			grid: {
				display: false,
			},
		},
		y: {
			beginAtZero: true,
			suggestedMax: 10,
			ticks: {
				stepSize: 1,
				callback: function (value) {
					return Number.isInteger(value) ? value : '';
				},
			},
		},
	},
};

const barOptions = {
	responsive: true,
	maintainAspectRatio: false,
	plugins: {
		legend: {
			display: true,
			position: 'top',
		},
	},
	scales: {
		x: {
			grid: {
				display: false,
			},
		},
		y: {
			beginAtZero: true,
			suggestedMax: 25,
			ticks: {
				stepSize: 5,
				callback: function (value) {
					return Number.isInteger(value) ? value : '';
				},
			},
		},
	},
};

/** Shopping Assistant line chart: allow Y-axis to grow past 10 (conversation / click volume). */
const shoppingAssistantLineOptions = {
	...options,
	scales: {
		...options.scales,
		y: {
			beginAtZero: true,
			ticks: {
				stepSize: 1,
				callback: function (value) {
					return Number.isInteger(value) ? value : "";
				},
			},
		},
	},
};

/** Map API series to full date-range labels (so X-axis always shows chosen calendar dates). */
function mapSeriesToDateRange(fullLabels, apiLabels, apiValues) {
	if (!apiLabels?.length || !apiValues?.length) return fullLabels.map(() => 0);
	return fullLabels.map((label) => {
		const i = apiLabels.findIndex((l) => String(l).trim() === String(label).trim());
		return i >= 0 ? (apiValues[i] ?? 0) : 0;
	});
}

/** Build date labels and zero series for the selected date range (like Search dashboard when no data). */
function buildEmptyChartData(startDate, endDate) {
	const labels = [];
	const interactions = [];
	const addToCart = [];
	const purchases = [];
	let current = new Date(startDate);
	const end = new Date(endDate);
	while (isBefore(current, end) || isEqual(current, end)) {
		labels.push(format(current, "MMM dd"));
		interactions.push(0);
		addToCart.push(0);
		purchases.push(0);
		current = addDays(current, 1);
	}
	if (labels.length === 0) {
		labels.push(format(new Date(startDate), "MMM dd"));
		interactions.push(0);
		addToCart.push(0);
		purchases.push(0);
	}
	return { labels, interactions, addToCart, purchases };
}

/** Empty series for Shopping Assistant chart (four lines + funnel-aligned). */
function buildEmptyChartDataShoppingAssistant(startDate, endDate) {
	const labels = [];
	const interactions = [];
	const productClicks = [];
	const addToCart = [];
	const purchases = [];
	let current = new Date(startDate);
	const end = new Date(endDate);
	while (isBefore(current, end) || isEqual(current, end)) {
		labels.push(format(current, "MMM dd"));
		interactions.push(0);
		productClicks.push(0);
		addToCart.push(0);
		purchases.push(0);
		current = addDays(current, 1);
	}
	if (labels.length === 0) {
		labels.push(format(new Date(startDate), "MMM dd"));
		interactions.push(0);
		productClicks.push(0);
		addToCart.push(0);
		purchases.push(0);
	}
	return { labels, interactions, productClicks, addToCart, purchases };
}

const ITEMS_PER_PAGE = 6;

const defaultLocalDateRange = {
	startDate: startOfMonth(new Date()),
	endDate: new Date(),
	key: "selection",
};

const AIAssistantDashboard = ({ embedded = false }) => {
	const productCtx = useContext(ProductContext);
	const { currentWebsiteId, dateRange: contextDateRange, setDateRange: setContextDateRange } = productCtx;
	const [localDateRange, setLocalDateRange] = useState(defaultLocalDateRange);
	const dateRange = embedded ? contextDateRange : localDateRange;
	const setDateRange = embedded ? setContextDateRange : setLocalDateRange;
	const [loader, setLoader] = useState(false);
	const [dashboardData, setDashboardData] = useState(null);
	const [shoppingAssistantLoader, setShoppingAssistantLoader] = useState(false);
	const [shoppingAssistantData, setShoppingAssistantData] = useState(null);
	const [widgetSkuPage, setWidgetSkuPage] = useState(1);

	const getReports = () => {
		const websiteId = getActiveWebsiteId(currentWebsiteId);
		if (!websiteId) {
			return;
		}
		setLoader(true);
		const payload = {
			websiteId,
			startDate: format(dateRange.startDate, "yyyy-MM-dd"),
			endDate: format(dateRange.endDate, "yyyy-MM-dd"),
		};
		PostData("ms1", "api/v2/aiassistant/AIAssistantDashboard", payload)
			.then((response) => {
				if (response !== "Invalid" && response !== "undefined") {
					if (response.status === "success") {
						setDashboardData(response.data);
					} else {
						toast.error(response.message);
						setDashboardData(null);
					}
				} else {
					setDashboardData(null);
				}
				setLoader(false);
			})
			.catch((err) => {
				console.error("Error fetching AI Assistant dashboard:", err);
				setDashboardData(null);
				setLoader(false);
			});
	};

	const getShoppingAssistantReports = () => {
		const websiteId = getActiveWebsiteId(currentWebsiteId);
		if (!websiteId) {
			return;
		}
		setShoppingAssistantLoader(true);
		const payload = {
			websiteId,
			startDate: format(dateRange.startDate, "yyyy-MM-dd"),
			endDate: format(dateRange.endDate, "yyyy-MM-dd"),
		};
		PostData("ms1", "api/v1/aiassistant/shopping-assistant-dashboard", payload)
			.then((response) => {
				if (response !== "Invalid" && response !== "undefined" && response?.status === "success") {
					setShoppingAssistantData(response.data);
				} else {
					setShoppingAssistantData(null);
				}
				setShoppingAssistantLoader(false);
			})
			.catch((err) => {
				console.error("Error fetching Shopping Assistant dashboard:", err);
				setShoppingAssistantData(null);
				setShoppingAssistantLoader(false);
			});
	};

	useEffect(() => {
		if (getActiveWebsiteId(currentWebsiteId)) {
			getReports();
			getShoppingAssistantReports();
		}
	}, [currentWebsiteId, dateRange.startDate, dateRange.endDate]);

	// Reset pagination when dashboard data is refetched
	useEffect(() => {
		setWidgetSkuPage(1);
	}, [dashboardData]);

	const hasData = dashboardData != null;
	const summary = dashboardData?.summary ?? null;

	// Summary cards: use API data or zeros when no data (like Search dashboard)
	const aiMetricsData = summary
		? {
				totalQuestionsAnswered: summary.totalQuestionsAnswered ?? 0,
				widgetRevenueImpact: summary.widgetRevenueImpact ?? 0,
				widgetConversionRate: summary.widgetConversionRate ?? 0,
				topConvertingSKU: summary.topConvertingSKU ?? { value: 0, sku: "" },
		  }
		: {
				totalQuestionsAnswered: 0,
				widgetRevenueImpact: 0,
				widgetConversionRate: 0,
				topConvertingSKU: { value: 0, sku: "-" },
		  };

	// Line chart: X-axis always shows chosen calendar dates; API data mapped to that range
	const conversionOverTime = dashboardData?.conversionOverTime;
	const emptyChart = buildEmptyChartData(dateRange.startDate, dateRange.endDate);
	const chartLabels = emptyChart.labels;
	const apiLabels = conversionOverTime?.labels ?? [];
	const widgetConversionData = {
		labels: chartLabels,
		datasets: [
			{ label: "Interactions", data: mapSeriesToDateRange(chartLabels, apiLabels, conversionOverTime?.interactions ?? []), fill: false, borderColor: "#8B5CF6", backgroundColor: "rgba(139, 92, 246, 0.1)", tension: 0.3 },
			{ label: "Add to Cart", data: mapSeriesToDateRange(chartLabels, apiLabels, conversionOverTime?.addToCart ?? []), fill: false, borderColor: "#10B981", backgroundColor: "rgba(16, 185, 129, 0.1)", tension: 0.3 },
			{ label: "Purchases", data: mapSeriesToDateRange(chartLabels, apiLabels, conversionOverTime?.purchases ?? []), fill: false, borderColor: "#3B82F6", backgroundColor: "rgba(59, 130, 246, 0.1)", tension: 0.3 },
		],
	};

	// Funnel: use API data or zeros (correct UI – 3 steps only)
	const funnel = dashboardData?.conversionFunnel;
	const conversionFunnelData = {
		labels: ["Widget Interactions", "Add to Cart", "Purchases"],
		datasets: [
			{
				label: "Conversions",
				data: funnel ? [funnel.widgetInteractions ?? 0, funnel.addToCart ?? 0, funnel.purchases ?? 0] : [0, 0, 0],
				backgroundColor: ["#3B82F6", "#10B981", "#8B5CF6"],
				borderColor: ["#3B82F6", "#10B981", "#8B5CF6"],
				borderWidth: 1,
			},
		],
	};

	// SKU table: use API items; when no data show empty and "No data yet" (like Search dashboard)
	const skuConversionItems = hasData && dashboardData?.skuConversion?.items ? dashboardData.skuConversion.items : [];
	const showSkuNoData = !loader && skuConversionItems.length === 0;

	// Conversion Funnel: show "No data yet" when all values are zero
	const funnelAllZero = !funnel || (funnel.widgetInteractions === 0 && funnel.addToCart === 0 && funnel.purchases === 0);
	const showFunnelNoData = !loader && funnelAllZero;

	// --- Shopping Assistant section (second API) ---
	const saSummary = shoppingAssistantData?.summary ?? null;
	const saMetricsData = saSummary
		? {
				totalConversations: saSummary.totalConversations ?? 0,
				productClicks: saSummary.productClicks ?? 0,
				assistantRevenueImpact: saSummary.assistantRevenueImpact ?? 0,
				assistantConversionRate: saSummary.assistantConversionRate ?? 0,
				topConvertingSKU: saSummary.topConvertingSKU ?? { value: 0, sku: "" },
		  }
		: {
				totalConversations: 0,
				productClicks: 0,
				assistantRevenueImpact: 0,
				assistantConversionRate: 0,
				topConvertingSKU: { value: 0, sku: "-" },
		  };
	const saOverTime = shoppingAssistantData?.conversionOverTime;
	// Shopping Assistant: four series (matches shopping-assistant-dashboard API + funnel)
	const saEmptyChart = buildEmptyChartDataShoppingAssistant(dateRange.startDate, dateRange.endDate);
	const saChartLabels = saEmptyChart.labels;
	const saApiLabels = saOverTime?.labels ?? [];
	const saWidgetConversionData = {
		labels: saChartLabels,
		datasets: [
			{
				label: "Interactions",
				data: mapSeriesToDateRange(saChartLabels, saApiLabels, saOverTime?.interactions ?? []),
				fill: false,
				borderColor: "#8B5CF6",
				backgroundColor: "rgba(139, 92, 246, 0.1)",
				tension: 0.3,
			},
			{
				label: "Product Clicks",
				data: mapSeriesToDateRange(saChartLabels, saApiLabels, saOverTime?.productClicks ?? []),
				fill: false,
				borderColor: "#F59E0B",
				backgroundColor: "rgba(245, 158, 11, 0.1)",
				tension: 0.3,
			},
			{
				label: "Add to Cart",
				data: mapSeriesToDateRange(saChartLabels, saApiLabels, saOverTime?.addToCart ?? []),
				fill: false,
				borderColor: "#10B981",
				backgroundColor: "rgba(16, 185, 129, 0.1)",
				tension: 0.3,
			},
			{
				label: "Purchases",
				data: mapSeriesToDateRange(saChartLabels, saApiLabels, saOverTime?.purchases ?? []),
				fill: false,
				borderColor: "#3B82F6",
				backgroundColor: "rgba(59, 130, 246, 0.1)",
				tension: 0.3,
			},
		],
	};
	const saFunnel = shoppingAssistantData?.conversionFunnel;
	const saFunnelLabels = ["Interactions", "Product Clicks", "Add to Cart", "Purchases"];
	const saFunnelValues = saFunnel
		? [
				saFunnel.widgetInteractions ?? 0,
				saFunnel.productClicks ?? 0,
				saFunnel.addToCart ?? 0,
				saFunnel.purchases ?? 0,
		  ]
		: [0, 0, 0, 0];
	const saConversionFunnelData = {
		labels: saFunnelLabels,
		datasets: [
			{
				label: "Conversions",
				data: saFunnelValues,
				backgroundColor: ["#8B5CF6", "#F59E0B", "#10B981", "#3B82F6"],
				borderColor: ["#8B5CF6", "#F59E0B", "#10B981", "#3B82F6"],
				borderWidth: 1,
			},
		],
	};
	const saFunnelAllZero = !saFunnel || (saFunnelValues.every((v) => v === 0));
	const showSaFunnelNoData = !shoppingAssistantLoader && saFunnelAllZero;

	// Pagination for Widget-Assisted SKU table (6 per page)
	const totalWidgetSkuPages = Math.max(1, Math.ceil(skuConversionItems.length / ITEMS_PER_PAGE));
	const paginatedWidgetSkuItems = skuConversionItems.slice(
		(widgetSkuPage - 1) * ITEMS_PER_PAGE,
		widgetSkuPage * ITEMS_PER_PAGE
	);

	// Show at most 3 page numbers between prev/next arrows (sliding window around current page)
	const getVisiblePageNumbers = (currentPage, totalPages) => {
		if (totalPages <= 3) return Array.from({ length: totalPages }, (_, i) => i + 1);
		const start = Math.max(1, Math.min(currentPage - 1, totalPages - 2));
		return [start, start + 1, start + 2].filter((p) => p <= totalPages);
	};

	const btnStyle = (active) => ({
		minWidth: "32px",
		height: "32px",
		padding: "0 8px",
		border: active ? "1px solid #3B82F6" : "1px solid #e5e7eb",
		borderRadius: "6px",
		backgroundColor: active ? "#3B82F6" : "#fff",
		color: active ? "#fff" : "#374151",
		fontSize: "13px",
		fontWeight: active ? 600 : 400,
		cursor: "pointer",
	});
	const arrowBtnStyle = (disabled) => ({
		...btnStyle(false),
		cursor: disabled ? "not-allowed" : "pointer",
		opacity: disabled ? 0.5 : 1,
	});

	return (
		<React.Fragment>
			<div>
				{!embedded && (
					<Row className="mt-3 align-items-center justify-content-between">
						<Col xs="auto">
							<h2>Dashboard</h2>
						</Col>
						<Col xs={"auto"}>
							<DateRangePickerComponent
								dateRange={dateRange}
								onSelect={(range) => setDateRange(range)}
							/>
						</Col>
					</Row>
				)}

				{/* AI Shopping Assistant Conversion - same structure as Search dashboard */}
				<div className="row mt-4">
					<Col xs="12">
						<div className="ai-metrics-section">
							<h3>AI Shopping Assistant Conversion</h3>
							<p className="text-muted">Metrics showing the effectiveness and impact of the widget on customer behavior.</p>
						</div>
					</Col>
				</div>

				{/* Key Metrics Cards - Spinner when loading (same as Search dashboard) */}
				<Row className="mt-3">
					<Col xs="12" sm="6" md="3" className="mb-3">
						<Card className="h-100" style={{boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"}}>
							<div style={{height: "4px", backgroundColor: "#3B82F6", borderRadius: "4px 4px 0 0"}}></div>
							<CardBody className="d-flex flex-column justify-content-between h-100">
								<div className="d-flex justify-content-between align-items-center mb-2">
									<span className="text-muted small">Total Questions Answered</span>
									<i className="fa fa-question-circle text-muted" />
								</div>
								<div>
									{loader ? (
										<Spinner style={{ width: "20px", height: "20px" }} />
									) : (
										<h3 className="mb-0 text-primary">{aiMetricsData.totalQuestionsAnswered}</h3>
									)}
								</div>
							</CardBody>
						</Card>
					</Col>
					<Col xs="12" sm="6" md="3" className="mb-3">
						<Card className="h-100" style={{boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"}}>
							<div style={{height: "4px", backgroundColor: "#10B981", borderRadius: "4px 4px 0 0"}}></div>
							<CardBody className="d-flex flex-column justify-content-between h-100">
								<div className="d-flex justify-content-between align-items-center mb-2">
									<span className="text-muted small">Widget Revenue Impact</span>
									<i className="fa fa-dollar text-muted" />
								</div>
								<div>
									{loader ? (
										<Spinner style={{ width: "20px", height: "20px" }} />
									) : (
										<h3 className="mb-0 text-success">${aiMetricsData.widgetRevenueImpact}</h3>
									)}
								</div>
							</CardBody>
						</Card>
					</Col>
					<Col xs="12" sm="6" md="3" className="mb-3">
						<Card className="h-100" style={{boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"}}>
							<div style={{height: "4px", backgroundColor: "#8B5CF6", borderRadius: "4px 4px 0 0"}}></div>
							<CardBody className="d-flex flex-column justify-content-between h-100">
								<div className="d-flex justify-content-between align-items-center mb-2">
									<span className="text-muted small">Widget Conversion Rate</span>
									<i className="fa fa-percentage text-muted" />
								</div>
								<div>
									{loader ? (
										<Spinner style={{ width: "20px", height: "20px" }} />
									) : (
										<h3 className="mb-0 text-info">{aiMetricsData.widgetConversionRate}%</h3>
									)}
								</div>
							</CardBody>
						</Card>
					</Col>
					<Col xs="12" sm="6" md="3" className="mb-3">
						<Card className="h-100" style={{boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"}}>
							<div style={{height: "4px", backgroundColor: "#F59E0B", borderRadius: "4px 4px 0 0"}}></div>
							<CardBody className="d-flex flex-column justify-content-between h-100">
								<div className="d-flex justify-content-between align-items-center mb-2">
									<span className="text-muted small">Top Converting SKU</span>
									<i className="fa fa-star text-muted" />
								</div>
								<div>
									{loader ? (
										<Spinner style={{ width: "20px", height: "20px" }} />
									) : (
										<h3 className="mb-0 text-warning">{aiMetricsData.topConvertingSKU.sku || "-"}</h3>
									)}
								</div>
							</CardBody>
						</Card>
					</Col>
				</Row>

				{/* Widget Conversion Metrics Over Time - CredentialSkeleton when loading (same as Search) */}
				<Row className="mt-4">
					<Col xs="12">
						<Card className="h-100" style={{boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"}}>
							<CardBody className="p-3">
								<h4 className="mb-3">Widget Conversion Metrics Over Time <i className="fa fa-info-circle" style={{fontSize: "14px", color: "#6B7280"}}></i></h4>
								<div style={{height: "400px", width: "100%"}}>
									{loader ? (
										<CredentialSkeleton height={40} />
									) : (
										<Line data={widgetConversionData} options={options} />
									)}
								</div>
							</CardBody>
						</Card>
					</Col>
				</Row>

				{/* Conversion Funnel and SKU Table - same loader / No data yet format as Search */}
				<Row className="mt-4">
					<Col xs="12" md="6" className="mb-3">
						<Card className="h-100" style={{boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"}}>
							<CardBody className="p-3">
								<h4 className="mb-3">Conversion Funnel <i className="fa fa-info-circle" style={{fontSize: "14px", color: "#6B7280"}}></i></h4>
								<div style={{ height: "350px", width: "100%", position: "relative" }}>
									{loader ? (
										<CredentialSkeleton height={40} />
									) : (
										<>
											<Bar data={conversionFunnelData} options={barOptions} />
											{showFunnelNoData && (
												<div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
													<div className="noDataStatsText" style={{ position: "relative", bottom: "auto", left: "auto", transform: "none" }}>No data yet</div>
												</div>
											)}
										</>
									)}
								</div>
							</CardBody>
						</Card>
					</Col>
					<Col xs="12" md="6" className="mb-3">
						<Card className="h-100" style={{ boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)" }}>
							<CardBody className="p-3">
								<div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "12px" }}>
									<h4 style={{ margin: 0, fontWeight: "bold", color: "#374151", fontSize: "1rem", lineHeight: 1.4 }}>
										Widget-Assisted Conversion Rate per SKU
									</h4>
									<i className="fa fa-info-circle" style={{ fontSize: "14px", color: "#6B7280", flexShrink: 0 }} aria-hidden="true" />
								</div>
								<div style={{ position: "relative", minHeight: "300px", border: "1px solid #e5e7eb", borderRadius: "6px", overflow: "hidden", backgroundColor: "#fff" }}>
									<table style={{ width: "100%", borderCollapse: "collapse", margin: 0, tableLayout: "fixed" }}>
										<thead>
											<tr style={{ backgroundColor: "#f3f4f6", borderBottom: "1px solid #e5e7eb" }}>
												<th style={{ padding: "10px 14px", textAlign: "left", fontWeight: "600", color: "#4b5563", fontSize: "13px", border: "none" }}>SKU</th>
												<th style={{ padding: "10px 14px", textAlign: "left", fontWeight: "600", color: "#4b5563", fontSize: "13px", border: "none" }}>Widget Interactions</th>
												<th style={{ padding: "10px 14px", textAlign: "left", fontWeight: "600", color: "#4b5563", fontSize: "13px", border: "none" }}>Conversions</th>
												<th style={{ padding: "10px 14px", textAlign: "left", fontWeight: "600", color: "#4b5563", fontSize: "13px", border: "none" }}>Conversion Rate</th>
											</tr>
										</thead>
										<tbody>
											{loader ? (
												Array.from({ length: 5 }, (_, index) => (
													<tr key={index} style={{ borderBottom: "1px solid #f3f4f6" }}>
														<td style={{ padding: "10px 14px", border: "none" }}><Skeleton height={20} /></td>
														<td style={{ padding: "10px 14px", border: "none" }}><Skeleton height={20} /></td>
														<td style={{ padding: "10px 14px", border: "none" }}><Skeleton height={20} /></td>
														<td style={{ padding: "10px 14px", border: "none" }}><Skeleton height={20} /></td>
													</tr>
												))
											) : showSkuNoData ? (
												Array.from({ length: 6 }, (_, index) => (
													<tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#fafafa" : "#fff", borderBottom: "1px solid #f3f4f6" }}>
														<td style={{ padding: "12px 14px", height: "44px", border: "none", verticalAlign: "middle" }}>&nbsp;</td>
														<td style={{ padding: "12px 14px", border: "none" }}></td>
														<td style={{ padding: "12px 14px", border: "none" }}></td>
														<td style={{ padding: "12px 14px", border: "none" }}></td>
													</tr>
												))
											) : (
												paginatedWidgetSkuItems.map((item, index) => (
													<tr key={item.sku ?? index} style={{ backgroundColor: index % 2 === 0 ? "#fafafa" : "#fff", borderBottom: "1px solid #f3f4f6" }}>
														<td style={{ padding: "10px 14px", color: "#374151", fontSize: "13px", border: "none" }}>{item.sku}</td>
														<td style={{ padding: "10px 14px", color: "#374151", fontSize: "13px", border: "none" }}>{item.interactions}</td>
														<td style={{ padding: "10px 14px", color: "#374151", fontSize: "13px", border: "none" }}>{item.conversions}</td>
														<td style={{ padding: "10px 14px", color: "#374151", fontSize: "13px", border: "none" }}>{item.conversionRate}%</td>
													</tr>
												))
											)}
										</tbody>
									</table>
									{!showSkuNoData && skuConversionItems.length > ITEMS_PER_PAGE && (
										<div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "12px", borderTop: "1px solid #e5e7eb" }}>
											<button
												type="button"
												aria-label="Previous page"
												onClick={() => setWidgetSkuPage((p) => Math.max(1, p - 1))}
												disabled={widgetSkuPage <= 1}
												style={arrowBtnStyle(widgetSkuPage <= 1)}
											>
												<i className="fa fa-chevron-left" />
											</button>
											{getVisiblePageNumbers(widgetSkuPage, totalWidgetSkuPages).map((pageNum) => (
												<button
													key={pageNum}
													type="button"
													onClick={() => setWidgetSkuPage(pageNum)}
													style={btnStyle(widgetSkuPage === pageNum)}
												>
													{pageNum}
												</button>
											))}
											<button
												type="button"
												aria-label="Next page"
												onClick={() => setWidgetSkuPage((p) => Math.min(totalWidgetSkuPages, p + 1))}
												disabled={widgetSkuPage >= totalWidgetSkuPages}
												style={arrowBtnStyle(widgetSkuPage >= totalWidgetSkuPages)}
											>
												<i className="fa fa-chevron-right" />
											</button>
										</div>
									)}
									{showSkuNoData && (
										<div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
											<div style={{ display: "inline-block", padding: "12px 32px", backgroundColor: "#f9fafb", boxShadow: "0 1px 2px rgba(0,0,0,0.06)", border: "1px solid #e5e7eb", borderRadius: "6px", color: "#6b7280", fontSize: "14px" }}>
												No data yet
											</div>
										</div>
									)}
								</div>
							</CardBody>
						</Card>
					</Col>
				</Row>

				{/* Shopping Assistant section (separate API: shopping-assistant-dashboard) */}
				<Row className="mt-5">
					<Col xs="12">
						<div className="ai-metrics-section">
							<h3>Shopping Assistant</h3>
							<p className="text-muted">Metrics showing the effectiveness and impact of the assistant on customer behavior.</p>
						</div>
					</Col>
				</Row>
				<Row className="mt-3">
					<Col xs="12" sm="6" md="4" lg="2" className="mb-3">
						<Card className="h-100" style={{ boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)" }}>
							<div style={{ height: "4px", backgroundColor: "#3B82F6", borderRadius: "4px 4px 0 0" }}></div>
							<CardBody className="d-flex flex-column justify-content-between h-100">
								<div className="d-flex justify-content-between align-items-center mb-2">
									<span className="text-muted small">Total Conversations</span>
									<i className="fa fa-comments text-muted" />
								</div>
								<div>
									{shoppingAssistantLoader ? (
										<Spinner style={{ width: "20px", height: "20px" }} />
									) : (
										<h3 className="mb-0 text-primary">{saMetricsData.totalConversations}</h3>
									)}
								</div>
							</CardBody>
						</Card>
					</Col>
					<Col xs="12" sm="6" md="4" lg="2" className="mb-3">
						<Card className="h-100" style={{ boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)" }}>
							<div style={{ height: "4px", backgroundColor: "#F59E0B", borderRadius: "4px 4px 0 0" }}></div>
							<CardBody className="d-flex flex-column justify-content-between h-100">
								<div className="d-flex justify-content-between align-items-center mb-2">
									<span className="text-muted small">Product Clicks</span>
									<i className="fa fa-hand-pointer text-muted" />
								</div>
								<div>
									{shoppingAssistantLoader ? (
										<Spinner style={{ width: "20px", height: "20px" }} />
									) : (
										<h3 className="mb-0" style={{ color: "#D97706" }}>
											{saMetricsData.productClicks}
										</h3>
									)}
								</div>
							</CardBody>
						</Card>
					</Col>
					<Col xs="12" sm="6" md="4" lg="2" className="mb-3">
						<Card className="h-100" style={{ boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)" }}>
							<div style={{ height: "4px", backgroundColor: "#10B981", borderRadius: "4px 4px 0 0" }}></div>
							<CardBody className="d-flex flex-column justify-content-between h-100">
								<div className="d-flex justify-content-between align-items-center mb-2">
									<span className="text-muted small">Assistant Revenue Impact</span>
									<i className="fa fa-dollar text-muted" />
								</div>
								<div>
									{shoppingAssistantLoader ? (
										<Spinner style={{ width: "20px", height: "20px" }} />
									) : (
										<h3 className="mb-0 text-success">${saMetricsData.assistantRevenueImpact}</h3>
									)}
								</div>
							</CardBody>
						</Card>
					</Col>
					<Col xs="12" sm="6" md="4" lg="2" className="mb-3">
						<Card className="h-100" style={{ boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)" }}>
							<div style={{ height: "4px", backgroundColor: "#8B5CF6", borderRadius: "4px 4px 0 0" }}></div>
							<CardBody className="d-flex flex-column justify-content-between h-100">
								<div className="d-flex justify-content-between align-items-center mb-2">
									<span className="text-muted small">Assistant Conversion rate</span>
									<i className="fa fa-percentage text-muted" />
								</div>
								<div>
									{shoppingAssistantLoader ? (
										<Spinner style={{ width: "20px", height: "20px" }} />
									) : (
										<h3 className="mb-0 text-info">{saMetricsData.assistantConversionRate}%</h3>
									)}
								</div>
							</CardBody>
						</Card>
					</Col>
					<Col xs="12" sm="6" md="4" lg="2" className="mb-3">
						<Card className="h-100" style={{ boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)" }}>
							<div style={{ height: "4px", backgroundColor: "#F97316", borderRadius: "4px 4px 0 0" }}></div>
							<CardBody className="d-flex flex-column justify-content-between h-100">
								<div className="d-flex justify-content-between align-items-center mb-2">
									<span className="text-muted small">Top Converting SKU</span>
									<i className="fa fa-star text-muted" />
								</div>
								<div>
									{shoppingAssistantLoader ? (
										<Spinner style={{ width: "20px", height: "20px" }} />
									) : (
										<h3 className="mb-0 text-warning">{saMetricsData.topConvertingSKU.sku || "-"}</h3>
									)}
								</div>
							</CardBody>
						</Card>
					</Col>
				</Row>
				<Row className="mt-4">
					<Col xs="12">
						<Card className="h-100" style={{ boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)" }}>
							<CardBody className="p-3">
								<h4 className="mb-3">Assistant Conversion Metrics Over Time <i className="fa fa-info-circle" style={{ fontSize: "14px", color: "#6B7280" }}></i></h4>
								<div style={{ height: "400px", width: "100%" }}>
									{shoppingAssistantLoader ? (
										<CredentialSkeleton height={40} />
									) : (
										<Line data={saWidgetConversionData} options={shoppingAssistantLineOptions} />
									)}
								</div>
							</CardBody>
						</Card>
					</Col>
				</Row>
				<Row className="mt-4">
					<Col xs="12" className="mb-3">
						<Card className="h-100" style={{ boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)" }}>
							<CardBody className="p-3">
								<h4 className="mb-3">Conversion Funnel <i className="fa fa-info-circle" style={{ fontSize: "14px", color: "#6B7280" }}></i></h4>
								<div style={{ height: "350px", width: "100%", position: "relative" }}>
									{shoppingAssistantLoader ? (
										<CredentialSkeleton height={40} />
									) : (
										<>
											<Bar data={saConversionFunnelData} options={barOptions} />
											{showSaFunnelNoData && (
												<div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
													<div className="noDataStatsText" style={{ position: "relative", bottom: "auto", left: "auto", transform: "none" }}>No data yet</div>
												</div>
											)}
										</>
									)}
								</div>
							</CardBody>
						</Card>
					</Col>
				</Row>
			</div>
		</React.Fragment>
	);
}

export default AIAssistantDashboard;
